import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { usePhaseSettings, useUpdatePhaseDefaultProduct } from '@/hooks/usePhaseSettings';
import { Star } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useProducts } from '@/hooks/useProducts';

export default function InstallationPhasesManager() {
  const { data: phaseSettings, isLoading } = usePhaseSettings();
  const updateDefaultProduct = useUpdatePhaseDefaultProduct();
  const { products } = useProducts();

  const handleUpdateDefaultProduct = (phaseId: string, productId: string | null) => {
    updateDefaultProduct.mutate({
      id: phaseId,
      default_product_id: productId,
    });
  };

  const getProductsForPhase = (phaseKey: string) => {
    return products?.filter(p => p.install_phase === phaseKey && p.is_active) || [];
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
              <Skeleton className="h-6 w-12" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {phaseSettings?.map((phase) => {
          const phaseProducts = getProductsForPhase(phase.phase_key);
          const defaultProduct = products?.find(p => p.id === phase.default_product_id);
          
          return (
            <Card key={phase.id} className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm font-medium text-muted-foreground">
                    Phase {phase.display_order}
                  </span>
                  <span className="text-base font-semibold">
                    {phase.phase_title}
                  </span>
                </div>

                {/* Default Product Selection */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-primary" />
                    <Label className="text-sm font-medium">Default Product (Optional)</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This product will be auto-selected when entering this phase in sales presentation mode
                  </p>
                  <div className="flex items-center gap-3">
                    <Select
                      value={phase.default_product_id || "none"}
                      onValueChange={(value) => 
                        handleUpdateDefaultProduct(phase.id, value === "none" ? null : value)
                      }
                      disabled={updateDefaultProduct.isPending}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="No default product" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No default product</SelectItem>
                        {phaseProducts.map(product => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name} {product.brand && `(${product.brand})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {phase.default_product_id && defaultProduct && (
                      <Badge variant="secondary" className="whitespace-nowrap">
                        <Star className="w-3 h-3 mr-1" />
                        {defaultProduct.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
